import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { TableDescriptor, ToolbarButton } from '../../components/kiene-table/table-descriptor';
import { Condition, Operator, TableOption } from '../../components/kiene-table/table-option';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import {
    KieneBooleanTableColumn,
    NumberTableColumn,
    PdfTableColumn,
    TableColumn,
} from '../../components/kiene-table/table-column';
import { FileUploadService } from '../../dialogs/file-upload-dialog/file-upload.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Gemeinkosten } from '../gemeinkosten/gemeinkosten';
import { GemeinkostenDetailsDialogComponent } from '../gemeinkosten/gemeinkosten-details-dialog/gemeinkosten-details-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import {
    ConfirmDialogInput,
    ConfirmDialogResult,
} from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import {
    FilterItem,
    FilterItemType,
} from '../../../../projects/kiene-core/src/lib/services/backend-api/classes/filter-item';
import { Router } from '@angular/router';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { GemeinkostenInvoiceKiService } from '../gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice-ki.service';
import { GemeinkostenInvoiceReviewDialogComponent } from '../gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice-review-dialog.component';
import { WawiInvoiceDraftDto } from '../gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice.models';
import { GemeinkostenBulkUploadDialogComponent } from '../gemeinkosten/gemeinkosten-bulk-upload-dialog/gemeinkosten-bulk-upload-dialog.component';
import { catchError, forkJoin, map, of } from 'rxjs';

@Component({
    selector: 'kiene-honorare-und-gehaelter',
    templateUrl: './honorare-und-gehaelter.component.html',
    styleUrls: ['./honorare-und-gehaelter.component.scss'],
})
export class HonorareUndGehaelterComponent implements OnInit {
    @ViewChild('invoiceKiFileInput') invoiceKiFileInput: ElementRef<HTMLInputElement>;

    tableDescriptor = new TableDescriptor();

    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly tableService: KieneTableService,
        private readonly localStorageService: LocalStorageService,
        private readonly messageService: MessageService,
        private readonly fileUploadService: FileUploadService,
        private readonly dialog: MatDialog,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly pdfService: PdfViewerDesktopService,
        private readonly invoiceKi: GemeinkostenInvoiceKiService,
    ) {}

    ngOnInit(): void {
        this.tableDescriptor.headline = 'Honorare und Gehälter';
        this.tableDescriptor.uniqueIdentifier = 'WawiHonorare';
        this.tableDescriptor.alternativeApiUrl = this.apiBaseUrl + 'kosten/read_honorare.php';
        this.tableDescriptor.route = '/wawi/bestellung-extern/honorareundgehaelter/';
        this.tableDescriptor.nameOfIdField = 'kosten_id';
        this.tableDescriptor.searchPlaceholder = 'ID, Artikel, Rechnung Nr., Kommentar…';

        this.tableDescriptor.createAllowed = true;
        this.tableDescriptor.standardCreate = false;
        this.tableDescriptor.addVoid = this.openGemeinkostenDetailsDialog.bind(this);
        this.tableDescriptor.fileUploadApi = 'kosten/update_pdf.php';

        const cols: TableColumn[] = [];
        cols.push(new TableColumn('kosten_id', 'ID'));
        if (this.localStorageService.currentUserHasPermission(4215)) {
            cols.push(new PdfTableColumn('pdf_id', 'PDF', this.showPdf.bind(this)));
        }
        cols.push(new TableColumn('artikel_me', 'Artikel'));
        cols.push(new TableColumn('tierarzt', 'Tierarzt'));
        cols.push(new KieneBooleanTableColumn('labor', 'Labor'));
        cols.push(new TableColumn('kommentar', 'Kommentar'));
        cols.push(new TableColumn('rechnung_nr', 'Rechnung'));
        cols.push(new NumberTableColumn('kosten_netto_gesamt', 'Netto'));
        cols.push(new TableColumn('datum', 'Datum', 'date'));
        const lstgVon = new TableColumn('leistungsdatum_von', 'Lstg. von', 'date');
        lstgVon.isActive = false;
        cols.push(lstgVon);
        const lstgBis = new TableColumn('leistungsdatum_bis', 'Lstg. bis', 'date');
        lstgBis.isActive = false;
        cols.push(lstgBis);
        cols.push(
            new KieneBooleanTableColumn('scan_review', 'Scan', {
                clickable: {
                    void_: this.toggleScanReview.bind(this),
                    permissionId: 4212,
                    enabled: [new Condition('primanota_id', Operator.EXISTIERT_NICHT, null)],
                },
            }),
        );
        cols.push(
            new KieneBooleanTableColumn('freigegeben', 'Frei', {
                clickable: {
                    void_: this.freigeben.bind(this),
                    permissionId: 4216,
                    enabled: [new Condition('primanota_id', Operator.EXISTIERT_NICHT, null)],
                },
            }),
        );
        cols.push(new KieneBooleanTableColumn('primanota_id', 'PN', { tooltipFieldname: 'primanota_id' }));
        this.tableDescriptor.columns = cols;
        this.tableDescriptor.skipOptionsTablePadding = true;

        this.tableDescriptor.expandable = true;
        this.tableDescriptor.expandableFieldName = 'positionen';
        this.tableDescriptor.showDetailsExpandable = false;
        this.tableDescriptor.expandableLoad = (row: Gemeinkosten) => this.loadPositionenForRow(row);
        this.tableDescriptor.expandableSumColumns = ['kosten_netto'];
        this.tableDescriptor.expandableColumns = [
            new TableColumn('artikel_me', 'Artikel'),
            new TableColumn('bezeichnung', 'Bezeichnung'),
            new NumberTableColumn('kosten_netto', 'Netto'),
            new NumberTableColumn('mwstsatz', 'MwSt (%)'),
            new TableColumn('buchungsseite', 'S/H'),
            new TableColumn('konto_nr', 'Konto'),
        ];

        this.tableDescriptor.options.push(
            new TableOption(this.openScanReviewDialog.bind(this), 'Review', 'rate_review', 4212, {
                conditions: [new Condition('scan_review', Operator.GROESSER, 0)],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.reopenScanReview.bind(this), 'Nochmal reviewen', 'replay', 4212, {
                conditions: [
                    new Condition('scan_review', Operator.KLEINER_GLEICH, 0),
                    new Condition('invoice_draft_id', Operator.GROESSER, 0),
                ],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.updateHonorarDialog.bind(this), 'Editieren', 'edit', 4212, {
                conditions: [
                    new Condition('scan_review', Operator.KLEINER_GLEICH, 0),
                    new Condition('primanota_id', Operator.EXISTIERT_NICHT, null),
                ],
            }),
        );
        this.tableDescriptor.options.push(new TableOption(this.confirmAndDelete.bind(this), 'Löschen', 'delete', 4214));

        this.tableDescriptor.showDetails = false;
        this.tableDescriptor.showDetailsVoid = (g: Gemeinkosten) => {
            if (g?.primanota_id) {
                return;
            }
            this.openScanReviewDialog(g, g?.scan_review > 0 ? 'review' : 'edit');
        };

        this.tableDescriptor.permissionView = 4211;
        this.tableDescriptor.permissionCreate = 4212;

        this.tableDescriptor.filterItems.push(
            new FilterItem('Freigegeben', '1', FilterItemType.CHECKBOX, 'freigegeben', false),
        );
        this.tableDescriptor.filterItems.push(
            new FilterItem('Nicht Freigegeben', '0', FilterItemType.CHECKBOX, 'freigegeben', false),
        );
        this.tableDescriptor.filterItems.push(
            new FilterItem('Ohne Primanota', null, FilterItemType.CHECKBOX, 'primanota_id'),
        );
        this.tableDescriptor.filterItems.push(
            new FilterItem('Scan-Review', '1', FilterItemType.CHECKBOX, 'scan_review', true),
        );

        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.generatePrimanota.bind(this), 'account_balance', 1832, 'Primanota erstellen'),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.openInvoiceKiFilePicker.bind(this), 'document_scanner', 17002, 'Honorar scannen'),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(
                this.openInvoiceKiBulkPicker.bind(this),
                'library_add',
                17002,
                'Honorare Bulk (Scan-Warteschlange)',
            ),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.showKiBulkErrors.bind(this), 'error', 17002, 'Scan-Bulk Fehler'),
        );

        this.tableDescriptor.initialSortColumn = 'erstellt';
        this.tableDescriptor.initialSortDirection = 'desc';

        this.fileUploadService.watchFileUploaded().subscribe(result => {
            if (result) {
                this.tableService.reload();
            }
        });
    }

    showPdf(gemeinkosten: Gemeinkosten) {
        const input = new PdfViewerDialogInput(
            'kosten/read_pdf_honorare.php',
            new HttpParams().set('kosten_id', gemeinkosten.kosten_id),
            'Honorare',
        );
        this.pdfService.openPdfViewer(input);
    }

    private confirmAndDelete(event: Gemeinkosten) {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            data: { title: 'Löschen', text: 'Wollen Sie diesen Honorar-/Gehaltsbeleg wirklich löschen?' },
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.tableService.deleteEntity(this.apiBaseUrl + 'kosten/delete.php', event).subscribe(
                    () => {
                        this.messageService.infoMessage('Beleg wurde erfolgreich gelöscht!');
                        this.tableService.reload();
                    },
                    error => {
                        this.messageService.errorMessage('Beleg konnte nicht gelöscht werden!', error);
                        this.tableService.reload();
                    },
                );
            }
        });
    }

    generatePrimanota() {
        const input = new ConfirmDialogInput();
        input.headline = 'Primanota erstellen';
        input.text =
            'Soll die Primanota für alle offenen Positionen in den Reitern "Betriebliche Kosten" und "Honorare und Gehälter" erstellt werden?';
        input.okButton = 'Erstellen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { width: '50%', minWidth: '300px', data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.http.post(this.apiBaseUrl + 'primanota/generieren_lieferanten.php', { kosten: 1 }).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Die Primanota wurde erfolgreich generiert!');
                            this.router.navigate(['/finanzen', 'primanota']);
                        },
                        error: err => {
                            this.messageService.errorMessage(
                                'Es ist ein Fehler beim Generieren der Primanota aufgetreten!',
                                err,
                            );
                        },
                    });
                }
            },
        });
    }

    openGemeinkostenDetailsDialog(gemeinkosten?: Gemeinkosten) {
        const dialogRef = this.dialog.open(GemeinkostenDetailsDialogComponent, {
            data: { gemeinkosten: gemeinkosten, nurHonorar: true },
            maxWidth: '95vw',
            maxHeight: '90vh',
            autoFocus: false,
            panelClass: 'gemeinkosten-details-dialog-panel',
        });
        dialogRef.afterClosed().subscribe(() => this.tableService.reload());
    }

    updateHonorarDialog(gemeinkosten: Gemeinkosten) {
        if (gemeinkosten?.primanota_id) {
            return;
        }
        this.openScanReviewDialog(gemeinkosten, 'edit');
    }

    toggleScanReview(gemeinkosten: Gemeinkosten) {
        if (gemeinkosten.primanota_id) {
            return;
        }
        gemeinkosten.scan_review = gemeinkosten.scan_review > 0 ? 0 : 1;
        this.updateGemeinkosten(gemeinkosten);
    }

    freigeben(gemeinkosten: Gemeinkosten) {
        if (!gemeinkosten.primanota_id) {
            if (gemeinkosten.freigegeben > 0) {
                gemeinkosten.freigegeben = 0;
                this.updateGemeinkosten(gemeinkosten);
            } else {
                const input = new ConfirmDialogInput();
                input.headline = 'Freigabe';
                input.text = 'Position "' + (gemeinkosten.artikel_me || gemeinkosten.kosten_id) + '" freigeben?';
                input.okButton = 'Freigeben';

                const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });

                dialogRef.afterClosed().subscribe({
                    next: (accepted: ConfirmDialogResult) => {
                        if (accepted?.confirmed) {
                            gemeinkosten.freigegeben = 1;
                            gemeinkosten.scan_review = 0;
                            this.updateGemeinkosten(gemeinkosten);
                        } else {
                            this.tableService.reload();
                        }
                    },
                });
            }
        }
    }

    updateGemeinkosten(gemeinkosten: Gemeinkosten) {
        this.tableService.post(this.apiBaseUrl + 'kosten/update.php', gemeinkosten).subscribe({
            next: () => {
                this.messageService.infoMessage('Änderungen erfolgreich gespeichert!');
                this.tableService.reload();
            },
            error: err => {
                this.messageService.errorMessage('Änderungen konnten nicht gespeichert werden!', err);
                this.tableService.reload();
            },
        });
    }

    private loadPositionenForRow(row: Gemeinkosten) {
        const r = row as Gemeinkosten & { __expandLoaded?: boolean; positionen?: any[] };
        if (!r?.kosten_id) {
            r.positionen = Array.isArray(r.positionen) ? r.positionen : [];
            return of(r.positionen);
        }
        if (r.__expandLoaded && Array.isArray(r.positionen)) {
            return of(r.positionen);
        }
        return this.http
            .get<any>(this.apiBaseUrl + 'kosten/read_positionen.php', {
                params: new HttpParams().set('kosten_id', String(r.kosten_id)),
            })
            .pipe(
                map((res: any) => {
                    const positionen = Array.isArray(res?.positionen)
                        ? res.positionen
                        : Array.isArray(res?.records)
                          ? res.records
                          : [];
                    r.positionen = positionen.slice();
                    r.__expandLoaded = true;
                    return r.positionen;
                }),
            );
    }

    showKiBulkErrors() {
        this.invoiceKi.bulkFailed(40).subscribe({
            next: res => {
                const jobs = res.jobs ?? [];
                if (jobs.length === 0) {
                    this.messageService.infoMessage('Keine fehlgeschlagenen Scan-Bulk-Jobs.');
                    return;
                }
                const lines = jobs.map(j => `• ${j.fileName}${j.error ? ': ' + j.error : ''}`).join('\n');
                const input = new ConfirmDialogInput();
                input.headline = `Scan-Bulk Fehler (${jobs.length})`;
                input.text = lines;
                input.okButton = 'OK';
                input.showCancel = false;
                this.dialog.open(ConfirmDialogComponent, { data: input, width: '640px' });
            },
            error: err => {
                this.messageService.errorMessage('KI-Fehlerliste konnte nicht geladen werden.', err);
            },
        });
    }

    openInvoiceKiFilePicker() {
        const input = this.invoiceKiFileInput?.nativeElement;
        if (!input) {
            return;
        }
        input.value = '';
        input.click();
    }

    openInvoiceKiBulkPicker() {
        const dialogRef = this.dialog.open(GemeinkostenBulkUploadDialogComponent, {
            width: '560px',
            maxWidth: '95vw',
            autoFocus: false,
            panelClass: 'gemeinkosten-bulk-upload-dialog-panel',
        });
        dialogRef.afterClosed().subscribe((files: File[] | undefined) => {
            if (files?.length) {
                this.startInvoiceKiBulk(files);
            }
        });
    }

    onInvoiceKiFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const selected = input.files ? Array.from(input.files) : [];
        input.value = '';
        if (selected.length === 0) {
            return;
        }
        if (selected.length === 1) {
            this.startInvoiceKi(selected[0]);
            return;
        }
        this.startInvoiceKiBulk(selected);
    }

    private startInvoiceKi(file: File) {
        this.messageService.infoMessage('Rechnung wird analysiert und angelegt…');
        this.invoiceKi.preview(file).subscribe({
            next: draft => {
                const runId = draft?.runId;
                if (!runId) {
                    this.messageService.infoMessage('Keine runId vom Preview.');
                    return;
                }
                const dup = (draft.warnings ?? []).find(
                    (w: string) => typeof w === 'string' && w.toUpperCase().startsWith('DUPLIKAT:'),
                );
                if (dup) {
                    this.messageService.infoMessage(String(dup).replace(/^DUPLIKAT:\s*/i, ''));
                    return;
                }
                this.invoiceKi.autoCommit(runId, { honorar: true }).subscribe({
                    next: res => {
                        this.messageService.infoMessage(
                            res?.kostenId
                                ? `Honorar angelegt (ID ${res.kostenId}) – Filter „Scan-Review“ zum Prüfen.`
                                : 'Honorar angelegt – Filter „Scan-Review“ zum Prüfen.',
                        );
                        this.tableService.reload();
                    },
                    error: err => {
                        this.messageService.errorMessage(
                            'Auto-Anlage fehlgeschlagen (Artikel/Datum/Duplikat prüfen).',
                            err,
                        );
                    },
                });
            },
            error: err => {
                this.messageService.errorMessage('Rechnungs-Analyse fehlgeschlagen.', err);
            },
        });
    }

    private startInvoiceKiBulk(files: File[]) {
        this.messageService.infoMessage(
            `${files.length} Rechnungen in Warteschlange… Fertige erscheinen in der Tabelle (Filter „Scan-Review“).`,
        );
        this.invoiceKi.bulkEnqueue(files).subscribe({
            next: res => {
                this.messageService.infoMessage(
                    `Bulk gestartet (${res.jobCount} Dateien). Tabelle aktualisiert sich nach Abschluss.`,
                );
                this.pollBulkBatch(res.batchId);
            },
            error: err => {
                this.messageService.errorMessage('Bulk-Upload fehlgeschlagen.', err);
            },
        });
    }

    private pollBulkBatch(batchId: string) {
        const notifiedFailed = new Set<string>();
        const tick = () => {
            this.invoiceKi.bulkStatus(batchId).subscribe({
                next: status => {
                    for (const j of status.jobs ?? []) {
                        if (j.status === 'failed' && j.jobId && !notifiedFailed.has(j.jobId)) {
                            notifiedFailed.add(j.jobId);
                            this.messageService.infoMessage(
                                `KI-Bulk fehlgeschlagen: ${j.fileName}` + (j.error ? ` – ${j.error}` : ''),
                            );
                        }
                    }

                    const pending = (status.totals?.queued ?? 0) + (status.totals?.processing ?? 0);
                    if (pending > 0) {
                        setTimeout(tick, 3000);
                        return;
                    }

                    const ready = (status.jobs ?? []).filter(j => j.status === 'readyForReview' && !!j.runId);
                    const alreadyCommitted =
                        (status.totals?.committed ?? 0) ||
                        (status.jobs ?? []).filter(j => j.status === 'committed').length;
                    const extractFailed = status.totals?.failed ?? 0;

                    if (ready.length === 0) {
                        this.finishBulkBatch(alreadyCommitted, extractFailed, status.jobs ?? []);
                        return;
                    }

                    this.messageService.infoMessage(`Extract fertig – lege ${ready.length} Honorar(e) an…`);
                    forkJoin(
                        ready.map(j =>
                            this.invoiceKi.autoCommit(j.runId!, { honorar: true }).pipe(
                                catchError(err => {
                                    this.messageService.infoMessage(
                                        `KI-Bulk Anlage fehlgeschlagen: ${j.fileName}` +
                                            (err?.error?.message || err?.message
                                                ? ` – ${err?.error?.message || err?.message}`
                                                : ''),
                                    );
                                    return of(null);
                                }),
                            ),
                        ),
                    ).subscribe({
                        next: results => {
                            const committed = alreadyCommitted + results.filter(r => r != null).length;
                            const commitFailed = results.filter(r => r == null).length;
                            this.finishBulkBatch(committed, extractFailed + commitFailed, status.jobs ?? []);
                        },
                        error: err => {
                            this.messageService.errorMessage('Bulk-Anlage fehlgeschlagen.', err);
                        },
                    });
                },
                error: err => {
                    this.messageService.errorMessage('Bulk-Status konnte nicht geladen werden.', err);
                },
            });
        };
        setTimeout(tick, 2000);
    }

    private finishBulkBatch(
        committed: number,
        failed: number,
        jobs: { status: string; fileName: string; error?: string }[],
    ) {
        const failedNames = jobs
            .filter(j => j.status === 'failed')
            .map(j => j.fileName + (j.error ? ` (${j.error})` : ''))
            .join('; ');
        this.messageService.infoMessage(
            `Bulk fertig: ${committed} angelegt` +
                (failed > 0
                    ? `, ${failed} fehlgeschlagen` + (failedNames ? `: ${failedNames}` : '')
                    : '') +
                '. Filter „Scan-Review“ zum Prüfen.',
        );
        this.tableService.reload();
    }

    reopenScanReview(gemeinkosten: Gemeinkosten) {
        if (gemeinkosten.primanota_id) {
            this.messageService.infoMessage('Beleg hat bereits eine Primanota – Review nicht möglich.');
            return;
        }
        gemeinkosten.scan_review = 1;
        this.tableService.post(this.apiBaseUrl + 'kosten/update.php', gemeinkosten).subscribe({
            next: () => {
                this.messageService.infoMessage('Erneut in Scan-Review.');
                this.openScanReviewDialog(gemeinkosten);
            },
            error: err => {
                this.messageService.errorMessage('Konnte nicht erneut auf Scan-Review setzen.', err);
                this.tableService.reload();
            },
        });
    }

    openScanReviewDialog(gemeinkosten: Gemeinkosten, mode: 'review' | 'edit' = 'review') {
        const runId = this.resolveInvoiceRunId(gemeinkosten);
        if (mode === 'review') {
            this.messageService.infoMessage('Scan-Review wird geladen…');
        }

        const draft$ = runId
            ? this.invoiceKi.getDraft(runId).pipe(catchError(() => of(this.buildReviewDraftStub(gemeinkosten))))
            : of(this.buildReviewDraftStub(gemeinkosten));

        const kosten$ =
            gemeinkosten?.kosten_id > 0
                ? forkJoin({
                      header: this.tableService
                          .get(
                              this.apiBaseUrl + 'kosten/read_honorare.php',
                              new HttpParams().set('kosten_id', String(gemeinkosten.kosten_id)),
                          )
                          .pipe(catchError(() => of(null))),
                      positionen: this.loadPositionenForRow(gemeinkosten).pipe(catchError(() => of([]))),
                  }).pipe(
                      map(({ header, positionen }) => {
                          const records: any[] = header?.records || [];
                          const rec =
                              records.find(r => Number(r.kosten_id) === Number(gemeinkosten.kosten_id)) ||
                              records[0];
                          const merged = rec?.kosten_id
                              ? ({ ...gemeinkosten, ...rec } as Gemeinkosten)
                              : gemeinkosten;
                          merged.positionen = Array.isArray(positionen) ? positionen : merged.positionen || [];
                          return merged;
                      }),
                      catchError(() => of(gemeinkosten)),
                  )
                : of(gemeinkosten);

        const pdfFromKosten$ = gemeinkosten?.pdf_id
            ? this.invoiceKi
                  .getKostenPdfFile(gemeinkosten.kosten_id, 'rechnung.pdf', true)
                  .pipe(catchError(() => of(null as File | null)))
            : of(null as File | null);
        const pdfFromDraft$ = runId
            ? this.invoiceKi.getPdfFile(runId).pipe(catchError(() => of(null as File | null)))
            : of(null as File | null);

        const pdf$ = forkJoin({ kosten: pdfFromKosten$, draft: pdfFromDraft$ }).pipe(
            map(({ kosten, draft }) => kosten ?? draft ?? null),
        );

        forkJoin({ draft: draft$, pdfFile: pdf$, kosten: kosten$ }).subscribe({
            next: ({ draft, pdfFile, kosten }) => {
                if (!pdfFile && mode === 'review') {
                    this.messageService.infoMessage(
                        'PDF fehlt – Review trotzdem öffnen (Positionen prüfen/nachtragen).',
                    );
                }
                const dialogRef = this.dialog.open(GemeinkostenInvoiceReviewDialogComponent, {
                    data: {
                        draft,
                        pdfFile: pdfFile ?? null,
                        gemeinkosten: kosten,
                        mode,
                        nurHonorar: true,
                    },
                    width: '100vw',
                    maxWidth: '100vw',
                    height: '100vh',
                    maxHeight: '100vh',
                    autoFocus: false,
                    panelClass: ['gemeinkosten-scan-review-dialog-panel', 'mat-dialog-no-padding'],
                });
                dialogRef.afterClosed().subscribe(() => this.tableService.reload());
            },
            error: err => {
                this.messageService.errorMessage(
                    mode === 'edit'
                        ? 'Bearbeiten konnte nicht geladen werden.'
                        : 'Scan-Review konnte nicht geladen werden.',
                    err,
                );
                const dialogRef = this.dialog.open(GemeinkostenInvoiceReviewDialogComponent, {
                    data: {
                        draft: this.buildReviewDraftStub(gemeinkosten),
                        pdfFile: null,
                        gemeinkosten,
                        mode,
                        nurHonorar: true,
                    },
                    width: '100vw',
                    maxWidth: '100vw',
                    height: '100vh',
                    maxHeight: '100vh',
                    autoFocus: false,
                    panelClass: ['gemeinkosten-scan-review-dialog-panel', 'mat-dialog-no-padding'],
                });
                dialogRef.afterClosed().subscribe(() => this.tableService.reload());
            },
        });
    }

    private resolveInvoiceRunId(g: Gemeinkosten): string {
        if (g?.wawi_run_id) {
            return String(g.wawi_run_id);
        }
        if (g?.invoice_draft_id != null && Number(g.invoice_draft_id) > 0) {
            return String(g.invoice_draft_id);
        }
        return '';
    }

    private buildReviewDraftStub(g: Gemeinkosten): WawiInvoiceDraftDto {
        const toIso = (v: any) => (v ? new Date(v).toISOString() : undefined);
        return {
            runId: this.resolveInvoiceRunId(g),
            mandantId: 0,
            benutzerId: 0,
            suggestedTyp: g.tierarzt_id ? 2 : Number(g.labor) > 0 ? 1 : 0,
            suggestedRechnungNr: g.rechnung_nr,
            suggestedDatum: toIso(g.datum),
            suggestedLeistungsdatumVon: toIso(g.leistungsdatum_von),
            suggestedLeistungsdatumBis: toIso(g.leistungsdatum_bis),
            suggestedLieferantId: g.lieferant_id,
            suggestedLieferantName: g.lieferant,
            suggestedArtikelId: g.artikel_id,
            suggestedArtikelMe: g.artikel_me,
            suggestedTierarztId: g.tierarzt_id,
            suggestedTierarztName: g.tierarzt,
            kommentar: g.kommentar,
            preise: (g.preise ?? []).map(p => ({
                kosten_netto: p.kosten_netto,
                mwstsatz: p.mwstsatz,
            })),
            warnings: ['Draft-Anzeige aus Honorare-Zeile + PDF (InvoiceService).'],
        };
    }
}
